"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState, type ReactNode } from "react"

import { RecoverDeliveryCrmHandoffDialog } from "@/components/deliveries/recover-delivery-crm-handoff-dialog"
import { ReconcileDeliveryShipmentDialog } from "@/components/deliveries/reconcile-delivery-shipment-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useDeliveryShipmentsQuery } from "@/hooks/queries/delivery-shipments"
import { usePermissions } from "@/hooks/use-permissions"
import { DELIVERIES_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { DELIVERY_CRM_MAX_ATTEMPTS, DELIVERY_CRM_UNKNOWN_ERROR_CODE } from "@/lib/deliveries/crm-handoff-constants"
import type { DeliveryShipmentListItem } from "@/server-actions/delivery-shipments"
import { useLocale, useTranslations } from "next-intl"


export function DeliveryShipmentsPanel() {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryShipmentsPanel")
  const tCommonUi = useTranslations("Common")

  const { can } = usePermissions()
  const canView = can(DELIVERIES_PERMISSIONS.VIEW)
  const canReconcile = can(DELIVERIES_PERMISSIONS.RECONCILE)
  const shipmentsQuery = useDeliveryShipmentsQuery({
    limit: 10,
    enabled: canView,
  })
  const [reconciling, setReconciling] = useState<DeliveryShipmentListItem | null>(
    null,
  )
  const [recoveringCrm, setRecoveringCrm] = useState<
    DeliveryShipmentListItem | null
  >(null)
  const shipments = shipmentsQuery.data?.pages.flatMap(page => page.shipments)
    ?? []

  if (!canView) return null

  let content: ReactNode
  if (shipmentsQuery.error) {
    content = (
      <Alert variant="destructive">
        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
          <span><span key={tUi("deliveryOperationsCouldNotBeLoaded")}>{tUi("deliveryOperationsCouldNotBeLoaded")}</span></span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void shipmentsQuery.refetch()}
          >
            <span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
        </AlertDescription>
      </Alert>
    )
  } else if (shipmentsQuery.isLoading) {
    content = <div className="h-24 animate-pulse rounded bg-muted" />
  } else if (shipments.length === 0) {
    content = (
      <p className="text-sm text-muted-foreground">
        <span key={tUi("noDeliveryOperationsHaveBeenRecordedYet")}>{tUi("noDeliveryOperationsHaveBeenRecordedYet")}</span></p>
    )
  } else {
    content = (
      <>
        <ShipmentTable
          shipments={shipments}
          canReconcile={canReconcile}
          onReconcile={setReconciling}
          onRecoverCrm={setRecoveringCrm}
        />
        {shipmentsQuery.hasNextPage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={shipmentsQuery.isFetchingNextPage}
            onClick={() => void shipmentsQuery.fetchNextPage()}
          >
            <span key={shipmentsQuery.isFetchingNextPage ? tCommonUi("loading") : tUi("loadMore")}>{shipmentsQuery.isFetchingNextPage ? tCommonUi("loading") : tUi("loadMore")}</span>
          </Button>
        ) : null}
      </>
    )
  }

  return (
    <div className="space-y-3 border-t pt-4">
      <div>
        <h3 className="text-sm font-medium"><span key={tUi("recentDeliveryOperations")}>{tUi("recentDeliveryOperations")}</span></h3>
        <p className="text-xs text-muted-foreground">
          <span key={tUi("waybillStatusTrackingDeliveryQuoteAndCRMHandoff")}>{tUi("waybillStatusTrackingDeliveryQuoteAndCRMHandoff")}</span></p>
      </div>

      {content}

      <ReconcileDeliveryShipmentDialog
        shipment={reconciling}
        onOpenChange={open => { if (!open) setReconciling(null) }}
      />
      <RecoverDeliveryCrmHandoffDialog
        shipment={recoveringCrm}
        onOpenChange={open => { if (!open) setRecoveringCrm(null) }}
      />
    </div>
  )
}

function ShipmentTable({
  shipments,
  canReconcile,
  onReconcile,
  onRecoverCrm,
}: {
  shipments: DeliveryShipmentListItem[]
  canReconcile: boolean
  onReconcile: (shipment: DeliveryShipmentListItem) => void
  onRecoverCrm: (shipment: DeliveryShipmentListItem) => void
}) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("Deliveries.DeliveriesDeliveryShipmentsPanel")

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead><span key={tUi("order")}>{tUi("order")}</span></TableHead>
          <TableHead><span key={tUi("recipient")}>{tUi("recipient")}</span></TableHead>
          <TableHead className="min-w-80"><span key={tUi("delivery")}>{tUi("delivery")}</span></TableHead>
          <TableHead><span key={tUi("productPayment")}>{tUi("productPayment")}</span></TableHead>
          <TableHead><span key={tUi("status")}>{tUi("status")}</span></TableHead>
          <TableHead><span key={tUi("tTN")}>{tUi("tTN")}</span></TableHead>
          <TableHead><span key={"CRM"}>{"CRM"}</span></TableHead>
          <TableHead><span key={tUi("created")}>{tUi("created")}</span></TableHead>
          {canReconcile ? <TableHead className="text-right"><span key={tUi("action")}>{tUi("action")}</span></TableHead> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {shipments.map(shipment => (
          <TableRow key={shipment.id}>
            <TableCell>
              <div className="max-w-64 font-medium">{shipment.orderName}</div>
              <div className="text-xs text-muted-foreground">
                {shipment.integrationName} <span>·</span> {shipment.orderReference}
              </div>
            </TableCell>
            <TableCell>
              <div className="font-medium">{shipment.recipientName ?? "—"}</div>
              <div className="whitespace-nowrap text-xs text-muted-foreground">
                {shipment.recipientPhone ?? tUi("noPhoneRecorded")}
              </div>
            </TableCell>
            <TableCell>
              <div className="font-medium">{shipment.deliveryMethod ?? "—"}</div>
              <div className="max-w-72 text-xs text-muted-foreground">
                {shipment.deliveryAddress ?? tUi("noDestinationRecorded")}
              </div>
              <div className="text-xs text-muted-foreground">
                <span><span key={tUi("fee")}>{tUi("fee")}</span></span> {formatMinorAmount(
                  shipment.carrierPriceMinor,
                  shipment.carrierPriceCurrency,
                )} <span><span key={tUi("paidBy")}>{tUi("paidBy")}</span></span> {shipment.deliveryFeePayer ?? tUi("unknown")}
              </div>
            </TableCell>
            <TableCell>
              <div className="font-medium">{shipment.merchandisePaymentMethod}</div>
              <div className="whitespace-nowrap text-xs text-muted-foreground">
                <span><span key={tUi("merchandise")}>{tUi("merchandise")}</span></span> {formatMinorAmount(
                  shipment.merchandiseTotalMinor,
                  shipment.merchandiseTotalCurrency,
                )}
              </div>
            </TableCell>
            <TableCell>
              <ShipmentStatusBadge status={shipment.operationStatus} />
              <ShipmentStatusDetails shipment={shipment} />
            </TableCell>
            <TableCell>{shipment.trackingNumber ?? "—"}</TableCell>
            <TableCell><CrmHandoffStatus shipment={shipment} /></TableCell>
            <TableCell className="whitespace-nowrap">
              {new Date(shipment.createdAt).toLocaleString(uiFormattingLocale)}
            </TableCell>
            {canReconcile ? (
              <TableCell className="text-right">
                <ShipmentRecoveryAction
                  shipment={shipment}
                  onReconcile={onReconcile}
                  onRecoverCrm={onRecoverCrm}
                />
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function ShipmentStatusBadge({
  status,
}: {
  status: DeliveryShipmentListItem["operationStatus"]
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryShipmentsPanel")

  switch (status) {
    case "creating":
      return <Badge variant="secondary"><span key={tUi("creating")}>{tUi("creating")}</span></Badge>
    case "created":
      return <Badge variant="default"><span key={tUi("created")}>{tUi("created")}</span></Badge>
    case "unknown":
      return <Badge variant="secondary"><span key={tUi("needsVerification")}>{tUi("needsVerification")}</span></Badge>
    case "failed":
      return <Badge variant="destructive"><span key={tUi("failed")}>{tUi("failed")}</span></Badge>
    case "cancelled":
      return <Badge variant="secondary"><span key={tUi("cancelled")}>{tUi("cancelled")}</span></Badge>
    default: {
      const exhaustiveStatus: never = status
      return exhaustiveStatus
    }
  }
}

function ShipmentStatusDetails({
  shipment,
}: {
  shipment: DeliveryShipmentListItem
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryShipmentsPanel")

  switch (shipment.operationStatus) {
    case "unknown":
      return (
        <p className="mt-1 max-w-64 text-xs text-muted-foreground">
          <span key={tUi("novaPostMayHaveCreatedThisWaybillCheck")}>{tUi("novaPostMayHaveCreatedThisWaybillCheck")}</span></p>
      )
    case "failed":
      return shipment.operationErrorCode ? (
        <div className="mt-1 max-w-48 break-words text-xs text-destructive">
          {shipment.operationErrorCode}
        </div>
      ) : null
    case "creating":
    case "created":
    case "cancelled":
      return null
    default: {
      const exhaustiveStatus: never = shipment.operationStatus
      return exhaustiveStatus
    }
  }
}

function CrmHandoffStatus({
  shipment,
}: {
  shipment: DeliveryShipmentListItem
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryShipmentsPanel")

  switch (shipment.crmSyncStatus) {
    case "not_required":
      return <Badge variant="outline"><span key={tUi("notRequired")}>{tUi("notRequired")}</span></Badge>
    case "pending":
      return (
        <div className="space-y-1">
          <Badge variant="outline"><span key={tUi("pending")}>{tUi("pending")}</span></Badge>
          <p key={shipment.operationStatus === "created"
              ? tUi("theWaybillExistsTheWorkerNotificationIsWaiting")
              : tUi("theManualAttentionNotificationIsWaitingToBe")} className="max-w-56 text-xs text-muted-foreground">
            {shipment.operationStatus === "created"
              ? tUi("theWaybillExistsTheWorkerNotificationIsWaiting")
              : tUi("theManualAttentionNotificationIsWaitingToBe")}
          </p>
        </div>
      )
    case "synced":
      return <Badge variant="outline"><span key={shipment.operationStatus === "created" ? tUi("workerNotified") : tUi("manualAttentionFlagged")}>{shipment.operationStatus === "created" ? tUi("workerNotified") : tUi("manualAttentionFlagged")}</span></Badge>
    case "failed":
      return <FailedCrmHandoffStatus shipment={shipment} />
    default: {
      const exhaustiveStatus: never = shipment.crmSyncStatus
      return exhaustiveStatus
    }
  }
}

function FailedCrmHandoffStatus({
  shipment,
}: {
  shipment: DeliveryShipmentListItem
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryShipmentsPanel")

  const locale = useLocale()
  let message = shipment.operationStatus === "created"
    ? tUi("theWaybillExistsButTheWorkerNotificationFailed")
    : tUi("theManualAttentionNotificationFailed")
  if (shipment.crmLastErrorCode === DELIVERY_CRM_UNKNOWN_ERROR_CODE) {
    message = tUi("checkCrmNote")
  } else if (shipment.crmSyncAttempts >= DELIVERY_CRM_MAX_ATTEMPTS) {
    message = tUi("retriesExhausted")
  } else if (shipment.crmNextAttemptAt) {
    message = tUi("retryScheduled", {when: new Date(shipment.crmNextAttemptAt).toLocaleString(locale)})
  }
  return (
    <div className="space-y-1">
      <Badge variant="destructive"><span key={tUi("workerNotNotified")}>{tUi("workerNotNotified")}</span></Badge>
      <p className="max-w-56 text-xs text-destructive">{message}</p>
    </div>
  )
}

function ShipmentRecoveryAction({
  shipment,
  onReconcile,
  onRecoverCrm,
}: {
  shipment: DeliveryShipmentListItem
  onReconcile: (shipment: DeliveryShipmentListItem) => void
  onRecoverCrm: (shipment: DeliveryShipmentListItem) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryShipmentsPanel")

  if (shipment.operationStatus === "unknown") {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onReconcile(shipment)}
        >
          <span key={tUi("verifyInNovaPost")}>{tUi("verifyInNovaPost")}</span></Button>
        {shipment.crmSyncStatus === "failed" ? (
          <Button type="button" size="sm" variant="outline" onClick={() => onRecoverCrm(shipment)}>
            <span key={tUi("recoverCRMHandoff")}>{tUi("recoverCRMHandoff")}</span></Button>
        ) : null}
      </div>
    )
  }
  if (
    ["created", "failed", "unknown"].includes(shipment.operationStatus)
    && shipment.crmSyncStatus === "failed"
  ) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onRecoverCrm(shipment)}
      >
        <span key={tUi("recoverCRMHandoff")}>{tUi("recoverCRMHandoff")}</span></Button>
    )
  }
  return null
}

function formatMinorAmount(amount: string | null, currency: string | null): string {
  if (!amount || !currency) return "—"
  const negative = amount.startsWith("-")
  const digits = negative ? amount.slice(1) : amount
  const padded = digits.padStart(3, "0")
  const major = `${padded.slice(0, -2)}.${padded.slice(-2)}`
  return `${negative ? "-" : ""}${major} ${currency}`
}
